import { Dispatch, SetStateAction } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Container, Draggable, OnDropCallback } from 'react-smooth-dnd'
import Add from '@mui/icons-material/Add'
import DragIndicator from '@mui/icons-material/DragIndicator'
import {
  Box,
  IconButton,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import useGetThemeColor from '@/hooks/useGetThemeColor'
import useLocationHelpers from '@/hooks/useLocationHelpers'
import useOverlay from '@/hooks/useOverlay'
import { WorkflowState, WorkflowStateType } from '@/types/workflows'
import { useSelectedWorkflowContext } from '../workflows/SelectedWorkflowProvider'
import AddEditWorkflowStateDialog from './AddEditWorkflowStateDialog'

type WorkflowStatesListProps = {
  onDrop: OnDropCallback
  isDragging: boolean
  setIsDragging: Dispatch<SetStateAction<boolean>>
  workflowStates?: WorkflowState[]
  workflowStateType?: WorkflowStateType
  listQueryKey?: QueryKey
  onCreateWorkflowState?: (state: WorkflowState) => void
  newWorkflowStateSortOrder?: number
}

export default function WorkflowStatesList({
  onDrop,
  isDragging,
  setIsDragging,
  workflowStates = [],
  workflowStateType,
  listQueryKey,
  onCreateWorkflowState,
  newWorkflowStateSortOrder,
}: WorkflowStatesListProps) {
  const theme = useTheme()
  const { '*': id } = useParams()
  const newWorkflowStateOverlay = useOverlay()
  const getThemeColor = useGetThemeColor()
  const { appendCurrentParamsToUrl } = useLocationHelpers()
  const { selectedWorkflow } = useSelectedWorkflowContext()

  return (
    <Box
      sx={{
        mb: 1,
      }}
    >
      {workflowStateType?.name && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block' }}
          >
            {workflowStateType.name}
          </Typography>

          {onCreateWorkflowState && (
            <>
              <IconButton
                size="small"
                color="primary"
                onClick={newWorkflowStateOverlay.open}
                sx={{ transform: 'translateX(2px)' }}
              >
                <Add fontSize="small" />
              </IconButton>
              <AddEditWorkflowStateDialog
                overlay={newWorkflowStateOverlay}
                workflowId={selectedWorkflow.id}
                workflowStateType={workflowStateType}
                listQueryKey={listQueryKey}
                onCreateWorkflowState={onCreateWorkflowState}
                newWorkflowStateSortOrder={newWorkflowStateSortOrder}
              />
            </>
          )}
        </Stack>
      )}
      {/* @ts-expect-error -- Container can have children */}
      <Container
        dragHandleSelector=".drag-handle"
        onDrop={onDrop}
        lockAxis="y"
        groupName="workflow-states"
        getGhostParent={() => document.body} // https://github.com/kutlugsahin/react-smooth-dnd/issues/56#issuecomment-545904390
        getChildPayload={(index) => {
          return {
            workflowState: workflowStates[index],
          }
        }}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={() => setIsDragging(false)}
        style={{
          border:
            isDragging && workflowStates.length === 0
              ? `dashed 1px ${theme.palette.divider}`
              : `dashed 1px transparent`,
          transition: 'border-color 0.2s',
          borderRadius: '4px',
        }}
      >
        {!isDragging && workflowStates.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ opacity: 0.5, lineHeight: '32px' }}
          >
            <i>None</i>
          </Typography>
        )}

        {workflowStates.map((state) => {
          const isInitialState =
            state.id === selectedWorkflow?.initial_workflow_state_id
          return (
            // @ts-expect-error -- Draggable can have children
            <Draggable key={state.id}>
              <Stack
                spacing={0.5}
                key={state.id}
                direction="row"
                alignItems="center"
              >
                <Box
                  sx={{
                    background: getThemeColor(state.color),
                    width: 4,
                    height: isInitialState ? 46 : 24,
                    borderRadius: 4,
                  }}
                />
                <ListItemButton
                  selected={state.id === id}
                  component={Link}
                  to={appendCurrentParamsToUrl(state.id)}
                  relative="path"
                  sx={{
                    borderRadius: 2,
                    py: 0,
                    px: 1,
                  }}
                >
                  <ListItemText
                    sx={{ flexGrow: 1 }}
                    primary={state.name}
                    secondary={isInitialState ? 'Initial State' : undefined}
                    slotProps={{
                      primary: {
                        noWrap: true,
                      },
                    }}
                  />
                </ListItemButton>

                <DragIndicator
                  className="drag-handle"
                  sx={{
                    cursor: 'grab',
                    color: 'text.secondary',
                    ':active': { cursor: 'grabbing' },
                  }}
                />
              </Stack>
            </Draggable>
          )
        })}
      </Container>
    </Box>
  )
}
